'use client';

/**
 * AI 整形結果プレビューコンポーネント
 *
 * AI が構造化したバグ報告を表示し、ユーザーが確認・編集できる。
 * - タイトルの編集可能
 * - セクションの内容を編集可能
 * - 類似 Issue のリンクを表示
 */

import { ExternalLink } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { AiFormatResult, SimilarIssue } from '@/lib/debug/types';

interface AiFormattedPreviewProps {
  result: AiFormatResult;
  similarIssues: SimilarIssue[];
  onUpdateTitle: (title: string) => void;
  onUpdateSection: (index: number, content: string) => void;
}

export function AiFormattedPreview({ result, similarIssues, onUpdateTitle, onUpdateSection }: AiFormattedPreviewProps) {
  return (
    <div className="space-y-4">
      {/* タイトル（編集可能） */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Issue タイトル</Label>
        <Input value={result.title} onChange={(e) => onUpdateTitle(e.target.value)} className="font-medium text-sm" />
      </div>

      {/* セクション */}
      <div className="space-y-3">
        {result.sections.map((section, index) => (
          <SectionEditor
            key={section.heading}
            heading={section.heading}
            content={section.content}
            isInferred={section.isInferred}
            onChange={(content) => onUpdateSection(index, content)}
          />
        ))}
      </div>

      {/* 類似 Issue */}
      <SimilarIssuesList issues={similarIssues} />
    </div>
  );
}

function SectionEditor({
  heading,
  content,
  isInferred,
  onChange,
}: {
  heading: string;
  content: string;
  isInferred: boolean;
  onChange: (content: string) => void;
}) {
  return (
    <div className="rounded-md border p-3 space-y-1">
      <Label className="text-xs font-medium">
        {heading}
        {isInferred && <span className="ml-1 text-amber-600 font-normal">(AI 推測)</span>}
      </Label>
      <Textarea value={content} onChange={(e) => onChange(e.target.value)} rows={3} className="text-sm" />
    </div>
  );
}

function SimilarIssuesList({ issues }: { issues: SimilarIssue[] }) {
  if (issues.length === 0) {
    return (
      <div className="text-xs text-muted-foreground p-2 border rounded-md">類似する Issue は見つかりませんでした</div>
    );
  }

  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">類似 Issue</Label>
      <div className="space-y-1">
        {issues.map((issue) => (
          <a
            key={issue.number}
            href={issue.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm p-2 rounded-md border hover:bg-muted/50 transition-colors"
          >
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
            <span className="text-muted-foreground">#{issue.number}</span>
            <span className="truncate">{issue.title}</span>
            <span
              className={`text-xs px-1 rounded ${
                issue.state === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {issue.state}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}
